import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { sortBy } from 'lodash'

import { ResolvedEngagementRequest, ResolvedEngagementResponse } from '@sparkai/shared-types'

import { apiClient } from '../apiClient'

export const RESOLVED_ENGAGEMENT_QUALITY_METRICS_KEY = 'quality-metric-resolved-engagement-v3'
export const RESOLVED_ENGAGEMENT_QUALITY_METRICS_INFINITE_KEY = `${RESOLVED_ENGAGEMENT_QUALITY_METRICS_KEY}-infinite`

const request = async (
  params: ResolvedEngagementRequest,
  pageParam: number
): Promise<ResolvedEngagementResponse> => {
  const { data } = await apiClient.get<ResolvedEngagementResponse>(`/int/v3/engagements/resolved`, {
    params: { ...params, cursor: pageParam },
    timeout: 30000,
  })
  return data
}

export function useGetResolved(params: ResolvedEngagementRequest) {
  return useQuery<ResolvedEngagementResponse>({
    queryKey: [RESOLVED_ENGAGEMENT_QUALITY_METRICS_KEY, params],
    queryFn: () => request({ ...params }, params.cursor),
    enabled: (Boolean(params?.programId) || Boolean(params?.systemIds)) && Boolean(params.limit === 10),
    refetchOnWindowFocus: false,
    select: (data) =>
      ({
        ...data,
        ids: data.resolvedEngagements.map((eng) => eng.engagementId),
      }) as ResolvedEngagementResponse,
  })
}

export function useGetResolvedWithInfiniteQuery(params: ResolvedEngagementRequest) {
  const { cursor, ...otherParams } = params

  const data = useInfiniteQuery<ResolvedEngagementResponse>({
    queryKey: [RESOLVED_ENGAGEMENT_QUALITY_METRICS_INFINITE_KEY, otherParams],
    queryFn: ({ pageParam = 0 }) => request({ ...params, cursor }, pageParam as number),
    refetchOnWindowFocus: false,
    enabled: (Boolean(params?.programId) || Boolean(params?.systemIds)) && Boolean(params?.limit === 16),
    initialPageParam: 0,
    getNextPageParam: () => {
      return params.cursor + params.limit
    },
  })

  const hasPages = data?.data?.pages && data?.data?.pages.length > 0
  const totalPending = hasPages ? data.data?.pages[0].total || 0 : 0
  const total = totalPending ?? 0

  const allItems = data?.data?.pages.flatMap((page) => page.resolvedEngagements) ?? []

  const seenIds = new Set<string>()
  const uniqueItems = allItems.filter((item) => {
    if (seenIds.has(item.engagementId)) {
      return false
    }
    seenIds.add(item.engagementId)
    return true
  })

  return {
    ...data,
    total,
    items: sortBy(uniqueItems, (item) => new Date(item.createdDate).getTime()).reverse(),
  }
}
